import { FocusEvent, InputHTMLAttributes, RefObject, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { useOutsideClick } from 'aem-libs-react-ts/src/hooks/useOutsideClick'
import { conditionalInvoke } from 'aem-libs-react-ts/src/utils/conditionalInvoke'
import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import { Maybe } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { objectKeys } from 'aem-libs-ts/src/utils/common/objectKeys'

import { CommonInputWrapper } from '../CommonInputWrapper'
import { CommonInputProps } from '../types'
import { useFieldError } from '../useFieldError'
import { useFieldMeta } from '../useFieldMeta'
import { ValidationMessage } from '../validation-message'
import * as StyledSelect from './SelectBox.styled'
import { SelectBoxItem, SelectItemsChangeOptions } from './SelectBoxItem'
import { useAriaController } from './hooks/useAriaController'
import { useFindOptionByValue } from './hooks/useFindOptionByValue'
import { useGroupedItems } from './hooks/useGroupItems'
import { useInputLabel } from './hooks/useInputLabel'
import { useSearchValue } from './hooks/useSearchValue'
import { useSelectBox } from './hooks/useSelectBox'
import { useSelectItems } from './hooks/useSelectItems'
import cs from './i18n/cs.json'
import en from './i18n/en.json'
import { defaultFilterFn, defaultFormatValueFn } from './selectHelper'
import { FilterFnType, FormatValue, GroupBy, RenderItem, RenderValue, SelectValueType } from './types'

// eslint-disable-next-line
interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onClick' | 'pattern' | 'width'> { }

interface BaseProps extends InputProps, CommonInputProps { }

const defaultHeight = 140

export interface SelectBoxProps<TValue extends SelectValueType, TOption> extends BaseProps {
  value: Maybe<TValue>
  onChange: (value: TValue, option: TOption) => void
  searchEnabled?: boolean
  simpleSearch?: boolean
  onSearchChange?: (searchString: string) => void
  searchValue?: string
  className?: string
  itemsHeight?: number | 'auto'
  items: TOption[]
  showAlwaysItemsCount?: number
  filterFunction?: FilterFnType<TOption>
  renderValue?: RenderValue<TOption>
  renderItem?: RenderItem<TOption>
  formatValue?: FormatValue<TOption, TValue>
  groupBy?: GroupBy<TOption>
  onClose?: () => void
}

export function SelectBox<TValue extends SelectValueType, TOption>({
  value,
  searchEnabled,
  className,
  onChange,
  itemsHeight = defaultHeight,
  label,
  items = [] as TOption[],
  onSearchChange,
  simpleSearch,
  showAlwaysItemsCount,
  validationMessage,
  tip,
  trackingDescription,
  hideValidation,
  renderValue,
  renderItem,
  filterFunction = defaultFilterFn as FilterFnType<TOption>,
  formatValue = defaultFormatValueFn as unknown as FormatValue<TOption, TValue>,
  groupBy,
  width,
  responsive,
  onClose,
  testId,
  ...inputProps
}: SelectBoxProps<TValue, TOption>) {
  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    id: inputProps.id,
    description: trackingDescription ?? label
  })

  const { t, i18n } = useTranslation('SelectBox')
  i18n.addResourceBundle('cs', 'SelectBox', cs)
  i18n.addResourceBundle('en', 'SelectBox', en)

  const inputRef: RefObject<HTMLInputElement | null> = useRef(null)
  const tryFindOptionByValue = useFindOptionByValue(items, formatValue)
  const { renderedItems, setRenderedItems } = useSelectItems(items, showAlwaysItemsCount)
  const { listRef, itemBaseId, isOpen, closeItems, switchOpen, openItems, setFocusedValue } = useSelectBox(
    renderedItems,
    value as TValue,
    {
      disabled: inputProps.disabled,
      searchEnabled
    }
  )

  const { searchValue, handleSearch, clearSearch } = useSearchValue(
    items,
    setRenderedItems,
    simpleSearch ?? false,
    filterFunction,
    onSearchChange
  )

  const groups = useGroupedItems(renderedItems, groupBy)

  const inputOptionLabel = useInputLabel(items, value as TValue, isOpen, searchEnabled, searchValue, formatValue)

  useEffect(() => {
    if (searchEnabled && isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const dropdownRef = useOutsideClick<HTMLDivElement, TOption[]>(
    {
      onClick: (args) => {
        clearSearch(args ?? [])
        closeItems()
        if (isOpen) {
          onClose && onClose()
        }
      }
    },
    items
  )

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (!searchEnabled && inputRef.current) {
      inputRef.current.blur()
    }
    conditionalInvoke(inputProps.onFocus, [e])
  }

  const handleBlur = () => {
    if (!searchEnabled) {
      inputRef?.current?.blur()
    }
  }

  const handleSelectItem = (option: TOption, options?: SelectItemsChangeOptions) => {
    const formattedValue = formatValue(option)
    trackInputChange(formattedValue, 'dropdown')
    if (searchEnabled && searchValue?.length) {
      clearSearch(items)
    }
    options?.forceClose ? closeItems() : switchOpen()
    onChange(formattedValue, option)
  }

  const isSearchInputVisible = () => {
    return !renderValue || (isOpen && searchEnabled)
  }

  const showSearchInput = isSearchInputVisible()
  const currentOption = tryFindOptionByValue(value as TValue)

  const ariaLabel = label ?? inputProps['aria-label']
  const { showError, ariaErrorProps, errorMessage } = useFieldError(inputProps.id, validationMessage, {
    hideValidation
  })

  const fieldProps = useFieldMeta(inputProps.id, { tip: tip }, ariaErrorProps)
  const usedTestId = testId ?? 'select-box'

  const { selectBoxRef, handleDropDownKeyAction, focusElement } = useAriaController<TOption>({
    dropdownRef: dropdownRef as RefObject<HTMLDivElement>,
    isOpen,
    renderedItems,
    handleSelectItem: (option, options) => {
      selectBoxRef.current?.focus()
      handleSelectItem(option, options)
    },
    clearSelectBox: () => {
      if (isOpen) {
        !searchEnabled && showSearchInput
          ? selectBoxRef.current?.focus()
          : selectBoxRef.current?.querySelector('input')?.focus()
        closeItems()
        onClose && onClose()
      }
    },
    switchOpen,
    showSearchInput
  })

  const focusElementRef = useCallback(
    (node: HTMLUListElement | null) => {
      if (isOpen && node) {
        const children = node.children
        const focusedIndex = value
          ? renderedItems.findIndex((item) => formatValue(item) === value)
          : parseInt(selectBoxRef.current?.getAttribute('data-focus-index') || '0')

        if (searchEnabled) {
          selectBoxRef.current?.setAttribute('data-focus-index', '-1')
          return
        }

        if (children.length > 0 && focusedIndex >= 0) {
          focusElement(selectBoxRef, focusedIndex)
        }
      }
    },
    [isOpen, renderedItems]
  )

  return (
    <StyledSelect.Container className={className} $width={width} responsive={responsive}>
      <CommonInputWrapper
        id={inputProps.id}
        htmlFor={inputProps.id}
        label={label}
        tip={tip}
        testId={usedTestId}
        tabIndex={-1}
      >
        <StyledSelect.Wrapper
          ref={selectBoxRef}
          id={'select-box-wrapper'}
          role={'combobox'}
          aria-controls='listbox'
          aria-haspopup='listbox'
          tabIndex={!searchEnabled && showSearchInput ? 0 : -1}
          aria-expanded={isOpen}
          onKeyDown={handleDropDownKeyAction}
          {...(isDefined(ariaLabel) && { ['aria-label']: ariaLabel })}
          {...fieldProps}
        >
          {!showSearchInput && (
            <StyledSelect.CustomValueLabel
              data-testid={createTestId(usedTestId, 'custom-label')}
              $valueWidth={width}
              disabled={inputProps.disabled ?? false}
              onClick={switchOpen}
              $isOpen={isOpen}
              $responsive={responsive}
              tabIndex={0}
            >
              {renderValue && currentOption && renderValue(currentOption)}
            </StyledSelect.CustomValueLabel>
          )}
          {showSearchInput && (
            <StyledSelect.Input
              ref={inputRef}
              aria-labelledby={inputProps.id}
              autoComplete='off'
              type='text'
              data-is-open={isOpen}
              value={inputOptionLabel}
              outerId={'select-box-input'}
              onClick={switchOpen}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => {
                !isOpen && renderedItems.length > 0 && openItems()
                handleSearch(e)
              }}
              hideValidation
              testId={usedTestId + '-selectbox'}
              isActive={isOpen && !searchEnabled}
              $searchEnabled={searchEnabled}
              tabIndex={searchEnabled ? 0 : -1}
              aria-label={t('SELECT_BOX.ARIA_LABEL')}
              {...inputProps}
            />
          )}
          <StyledSelect.Caret
            itemsVisible={isOpen}
            onClick={switchOpen}
            disabled={inputProps.disabled ?? false}
            tabIndex={-1}
          />
          {isOpen && (
            <StyledSelect.Items $itemsVisible={renderedItems.length > 0} role='listbox' tabIndex={-1}>
              <StyledSelect.ItemsInner
                id='items-list'
                data-testid='select-box-items'
                $itemsHeight={itemsHeight}
                ref={listRef}
                tabIndex={-1}
              >
                {!groups && (
                  <StyledSelect.OptionsList
                    ref={focusElementRef}
                    id={`${inputProps.id}-listbox`}
                    role='listbox'
                    data-testid={createTestId(usedTestId, inputId.items)}
                  >
                    {renderedItems.map((item, index) => (
                      <SelectBoxItem
                        item={item}
                        isFocused={value === formatValue(item)}
                        formatValue={formatValue}
                        renderItem={renderItem as RenderItem<TOption>}
                        dataItemId={itemBaseId.current as string}
                        testId={createTestId(usedTestId + '-item', index.toString())}
                        key={index}
                        onChange={handleSelectItem}
                      />
                    ))}
                  </StyledSelect.OptionsList>
                )}
                {groups &&
                  objectKeys(groups).map((groupName, groupIndex) => (
                    <div key={groupIndex}>
                      <StyledSelect.GroupLabel>{groupName}</StyledSelect.GroupLabel>
                      <StyledSelect.OptionsList>
                        {(groups[groupName] ?? []).map((groupItem, groupItemIndex) => (
                          <SelectBoxItem
                            isFocused={value === formatValue(groupItem)}
                            item={groupItem}
                            formatValue={formatValue}
                            renderItem={renderItem as RenderItem<TOption>}
                            dataItemId={itemBaseId.current as string}
                            key={groupItemIndex}
                            onFocus={() => setFocusedValue(formatValue(groupItem))}
                            onChange={handleSelectItem}
                          />
                        ))}
                      </StyledSelect.OptionsList>
                    </div>
                  ))}
              </StyledSelect.ItemsInner>
            </StyledSelect.Items>
          )}
        </StyledSelect.Wrapper>
        {showError && <ValidationMessage id={inputProps.id} message={errorMessage} testId={usedTestId} label={label} />}
      </CommonInputWrapper>
    </StyledSelect.Container>
  )
}
